import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import Spinner from "../../components/Spinner/Spinner";
import "./TransactionDetail.css";

const TransactionDetail = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const transactionId = state?.transaction?._id;

    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState("");
    const [actionType, setActionType] = useState("");

    const fetchTransaction = async () => {
        if (!transactionId) return;
        try {
            setLoading(true); setError("");
            const { data } = await privateApi.get(`/api/transactions/${transactionId}`);
            setTransaction(data.data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch transaction.");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        const initialFetchTransaction = async () => {
            if (!transactionId) return;
            try {
                setLoading(true); setError("");
                const { data } = await privateApi.get(`/api/transactions/${transactionId}`);
                setTransaction(data.data);
            } catch (err) {
                setError(err.response?.data?.message || "Failed to fetch transaction.");
            } finally { setLoading(false); }
        };

        initialFetchTransaction();
    }, [transactionId]);

    const handleActionClick = type => {
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setConfirmToken(token); setActionType(type); setConfirmOpen(true);
    };

    const handleConfirmAction = async input => {
        if (input !== confirmToken) {
            alert("Incorrect confirmation code."); setConfirmOpen(false); return;
        }
        try {
            setLoading(true);
            if (actionType === "reverse") await privateApi.post(`/api/transactions/${transaction._id}/reverse`);
            else if (actionType === "void") await privateApi.post(`/api/transactions/${transaction._id}/void`);
            await fetchTransaction();
        } catch (err) {
            alert(err.response?.data?.message || "Action failed.");
        } finally { setConfirmOpen(false); setActionType(""); }
    };

    if (loading) return <Spinner loadingInfo="Loading Transaction…" />;
    if (error) return (
        <div className="tx-page-centered">
            <div style={{ background: "var(--danger-bg)", border: "1px solid #F5C6C6", color: "var(--danger)", padding: "14px 20px", borderRadius: 10, fontSize: 14 }}>
                ⚠ {error}
            </div>
        </div>
    );
    if (!transaction) return null;

    const canReverse = transaction.status === "posted" && !transaction.isVoided;
    const canVoid = transaction.status !== "reversed" && !transaction.isVoided;
    const remaining = transaction.budgetLine
        ? (transaction.budgetLine.amount - transaction.budgetLine.spentAmount)
        : null;

    const Field = ({ label, children }) => (
        <div className="tx-detail-field">
            <span className="tx-detail-field-label">{label}</span>
            <span className="tx-detail-field-value">{children || "—"}</span>
        </div>
    );

    return (
        <div className="tx-page-centered">
            <div className="tx-detail-card">
                {/* Header bar */}
                <div className="tx-detail-header-bar">
                    <div>
                        <div className="tx-detail-ref">{transaction.referenceNumber}</div>
                    </div>
                    <div className="tx-detail-header-bar-right">
                        <span className={`tx-status ${transaction.status}`}>{transaction.status}</span>
                    </div>
                </div>

                {/* Amount highlight */}
                <div className="tx-amount-bar">
                    <div className="tx-amount-item">
                        <div className="tx-amount-label">Transaction Amount</div>
                        <div className="tx-amount-value">₦{Number(transaction.amount || 0).toLocaleString()}</div>
                    </div>
                    <div className="tx-amount-item">
                        <div className="tx-amount-label">Remaining on Budget Line</div>
                        <div className="tx-amount-value remaining">
                            {remaining !== null ? `₦${remaining.toLocaleString()}` : "—"}
                        </div>
                    </div>
                </div>

                {/* Fields */}
                <div className="tx-detail-fields">
                    <Field label="Budget Line">{transaction.budgetLine?.programmeId?.description}</Field>
                    <Field label="Type">
                        <span className={`tx-type ${transaction.type}`}>{transaction.type}</span>
                    </Field>
                    <Field label="Date">{new Date(transaction.date).toLocaleDateString()}</Field>
                    <Field label="Description">{transaction.description}</Field>
                </div>

                {/* Actions */}
                <div className="tx-detail-actions">
                    <button className="tx-btn tx-btn-secondary" onClick={() => navigate(-1)}>← Back</button>
                    {canReverse && (
                        <button className="tx-btn tx-btn-gold" onClick={() => handleActionClick("reverse")}>
                            ↩ Reverse
                        </button>
                    )}
                    {canVoid && (
                        <button className="tx-btn tx-btn-danger" onClick={() => handleActionClick("void")}>
                            ✕ Void
                        </button>
                    )}
                </div>
            </div>

            <ConfirmDialog show={confirmOpen} token={confirmToken}
                action={`You are about to ${actionType} this transaction`}
                onConfirm={handleConfirmAction}
                onCancel={() => { setConfirmOpen(false); setActionType(""); }} />
        </div>
    );
};

export default TransactionDetail;